import {announce} from '@github-ui/aria-live'
import {addSortToQuery, getSortFromQuery} from '@github-ui/dashboard-sorting-dropdown/query'
import type {Location} from '@github-ui/react-router'
import {useLocation, useNavigation} from '@github-ui/react-router'
import type {ReposListResults} from '@github-ui/repos-list'
import {
  getScreenReaderMessage,
  ReposList,
  ReposListLoadingSkeleton,
  ReposListProTip,
  sortingOptionsWithRelevance,
  useCompactModeSetting,
} from '@github-ui/repos-list'
import type {UserInfo} from '@github-ui/repos-list-blankslate'
import {getNoRepoMessage, NoReposMessage} from '@github-ui/repos-list-blankslate'
import {Pagination} from '@primer/react'
import {useEffect, useRef} from 'react'

import {useListResults} from '../hooks/use-list-results'
import type {PagePayload} from '../types'
import styles from './OrgReposListContent.module.css'

const DEFAULT_SORTING_KEY = 'updated'

function isSameLocation(a: Location, b: Location): boolean {
  return a.pathname === b.pathname && a.search === b.search
}

function getOrgReposScreenReaderMessage(
  isFetching: boolean,
  currentPage: number,
  fetchedQuery: string,
  results: ReposListResults,
  userInfo: UserInfo | undefined,
) {
  return getScreenReaderMessage(
    sortingOptionsWithRelevance,
    DEFAULT_SORTING_KEY,
    getNoRepoMessage,
    isFetching,
    currentPage,
    fetchedQuery,
    results,
    userInfo,
  )
}

interface OrgReposListContentProps {
  payload: PagePayload
  onQuerySubmit: (q: string) => void
}

export function OrgReposListContent({payload, onQuerySubmit}: OrgReposListContentProps) {
  const {repositories, repositoryCount, pageCount, userInfo, compactMode, searchable} = payload

  const navigation = useNavigation()
  const location = useLocation()
  const {fetchedQuery, currentPage, fetchResults} = useListResults()
  const {updateCompactModeSetting} = useCompactModeSetting(compactMode, 'orgs_repos_compact_mode')
  const reposListRef = useRef<HTMLUListElement>(undefined)

  const isNavigating = Boolean(navigation.location && !isSameLocation(navigation.location, location))

  const message = getOrgReposScreenReaderMessage(isNavigating, currentPage, fetchedQuery, payload, userInfo)
  useEffect(() => {
    if (!message) return

    const timeoutId = setTimeout(() => announce(message), 300)

    return () => clearTimeout(timeoutId)
  }, [message])

  if (isNavigating) {
    return <ReposListLoadingSkeleton />
  }

  return (
    <>
      <div className={styles.repositoriesListWrapper}>
        <div data-hpc>
          <ReposList
            listRef={reposListRef}
            query={fetchedQuery}
            showNameWithOwner={false}
            compactMode={compactMode}
            onCompactModeChanged={updateCompactModeSetting}
            repositoryCount={repositoryCount}
            repos={repositories}
            onSortingItemSelect={sort => onQuerySubmit(addSortToQuery(fetchedQuery, sort))}
            sortingItemSelected={getSortFromQuery(fetchedQuery)}
            defaultSortingKey={DEFAULT_SORTING_KEY}
            sortingOptions={sortingOptionsWithRelevance}
            directionRestrictedKeys={['relevance']}
            noReposMessage={
              <NoReposMessage
                currentPage={currentPage}
                filtered={!!fetchedQuery}
                pageCount={pageCount}
                userInfo={userInfo}
              />
            }
          />
        </div>
      </div>
      {pageCount > 1 && (
        <Pagination
          pageCount={pageCount}
          currentPage={currentPage}
          onPageChange={(e, page) => {
            // Avoid adding hash parameters
            e.preventDefault()

            reposListRef.current?.focus()
            window.scrollTo({top: 0})
            fetchResults({q: fetchedQuery, page})
          }}
        />
      )}
      {searchable && <ReposListProTip savedViewsAvailable={false} />}
    </>
  )
}
